import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NotificationManager } from "react-notifications";
import PurchaseOrderService from "../../services/purchaseOrder.service";

// Matches Diamond-PO-Prototype-v3.html's renderTray() -- a fixed bottom bar shown to retailers
// whenever they hold 1+ stones in their basket, hidden on the basket page itself.
// Unlike the prototype's fixed 222px sidebar offset, this spans the full viewport width
// since this app's sidebar is collapsible (no single fixed offset to hard-code).

export const BasketTray = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const loginDetials = useSelector((state) => state.loginReducer);

  const [stones, setStones] = useState([]);
  const [releasing, setReleasing] = useState(false);

  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;
  const memberType = loginDetials?.loginDetials?.responseData?.dealerMemberTypeName;
  const isRetailer = memberType === "Retailer" || memberType === "Combined";

  const loadBasket = () => {
    if (!dealerId || !isRetailer) return;
    PurchaseOrderService.GetMyDiamondHolds({ DealerID: String(dealerId) }).then((response) => {
      setStones(response?.data?.responseData?.Table || []);
    });
  };

  useEffect(() => {
    loadBasket();
    const handler = () => loadBasket();
    window.addEventListener("basketChanged", handler);
    return () => window.removeEventListener("basketChanged", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId, isRetailer]);

  useEffect(() => {
    loadBasket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (!isRetailer || stones.length === 0 || location.pathname === "/basket") return null;

  const supplierName = stones[0]?.SupplierCompany;
  const total = stones.reduce((sum, s) => sum + Number(s.Price || 0), 0);

  const handleReleaseAll = () => {
    setReleasing(true);
    Promise.all(
      stones.map((s) =>
        PurchaseOrderService.ReleaseDiamondHold({ DInventoryID: String(s.DInventoryID), DealerID: String(dealerId) })
      )
    ).then(() => {
      setReleasing(false);
      NotificationManager.success(t("Basket cleared — holds released"));
      loadBasket();
      window.dispatchEvent(new CustomEvent("basketChanged"));
    });
  };

  const goToBasket = (mode) => {
    navigate("/basket", { state: { mode } });
  };

  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        background: "#10182f",
        color: "#fff",
        zIndex: 90,
        boxShadow: "0 -8px 30px rgba(16,24,47,.28)",
        display: "flex",
        alignItems: "center",
        gap: 18,
        padding: "13px 24px",
      }}
    >
      <div
        style={{
          background: "#3b5bdb",
          width: 30,
          height: 30,
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 14,
          flex: "0 0 auto",
        }}
      >
        {stones.length}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <b style={{ fontSize: 14.5 }}>
          {t("{{count}} stone{{plural}} held from {{supplier}}", {
            count: stones.length,
            plural: stones.length > 1 ? "s" : "",
            supplier: supplierName,
          })}
        </b>
        <div style={{ fontSize: 11.5, color: "#9aa8c8", marginTop: 1 }}>
          {t("Send as a memo request or buy outright · holds expire in 2 hours")}
        </div>
      </div>
      <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 20, fontWeight: 700, letterSpacing: "-.01em" }}>
        ${total.toLocaleString()}
      </div>
      <button
        onClick={handleReleaseAll}
        disabled={releasing}
        style={{
          background: "none",
          border: "1px solid #3c4a6e",
          color: "#c3cbe4",
          borderRadius: 6,
          padding: "8px 12px",
          fontSize: 12.5,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {t("Release all")}
      </button>
      <button
        onClick={() => goToBasket("MEMO")}
        style={{
          background: "#3b5bdb",
          color: "#fff",
          border: "1px solid #3b5bdb",
          borderRadius: 6,
          padding: "8px 15px",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {t("Request on memo →")}
      </button>
      <button
        onClick={() => goToBasket("PURCHASE")}
        style={{
          background: "#2bb673",
          color: "#fff",
          border: "1px solid #2bb673",
          borderRadius: 6,
          padding: "8px 15px",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {t("Buy now →")}
      </button>
    </div>
  );
};

export default BasketTray;
